/* eslint-disable i18next/no-literal-string */
import { Box, Divider, Icon, Theme, Typography } from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import {
  faBirthdayCake,
  faCalendarAlt,
  faCheck,
  faClock,
  faLightbulb,
  faSignalStream,
  faUsers,
  faVideoSlash,
} from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import {
  useSelfElectedStateFundingProgram,
  useSession,
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  IconBullet,
  SelfPacedIconBullets,
  useActivityString,
  usePriceDetailsString,
} from "@outschool/ui-components-website";
import { useScrollToElement } from "@outschool/ui-utils";
import React, { SetStateAction, useCallback, useMemo } from "react";

import { useStickyHeader } from "../../../../hooks/useStickyHeader";
import { useMembershipRequirement } from "../../../subscriptions/hooks/useMembershipRequirement";
import { AskAQuestionButton } from "../../ctas/AskAQuestionButton";
import ChooseATimeOrClassScheduleRequestButton from "../../ctas/ChooseATimeOrClassScheduleRequestButton";
import { SectionCallToAction } from "../../ctas/SectionCallToActionButtons";
import TutorEnrollButtonAndModal from "../../ctas/TutorEnrollButtonAndModal";
import useTutoringFlow from "../../hooks/useTutoringFlow";
import PriceCreditsLineItem from "../../subcomponents/PriceCreditsLineItem";
import PriceLineItem from "../../subcomponents/PriceLineItem";
import { hasOneOnOneAvailabilities } from "../../utils";

export interface UnlockedDesktopActivityCardProps {
  activity: ClassDetailsQuery_ActivityFragment;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  isReadOnly?: boolean;
  onClickViewReviews?: (event: React.SyntheticEvent) => void;
  onClickMoreLikeThis?: () => void;
  sectionsRef?: React.RefObject<HTMLElement>;
  viewAvailabilityRef?: React.RefObject<HTMLElement>;
  setIsAvailabilityModalOpen?: React.Dispatch<SetStateAction<boolean>>;
  hideCreditDescription?: boolean;
}

export default function UnlockedDesktopActivityCard({
  activity,
  activityWithEnrollableTimes,
  leader,
  isReadOnly,
  onClickMoreLikeThis,
  sectionsRef,
  viewAvailabilityRef,
  setIsAvailabilityModalOpen,
  hideCreditDescription = false,
}: UnlockedDesktopActivityCardProps) {
  const { isLoggedIn } = useSession();
  const { t } = useTranslation(
    "client\\components\\detailPage\\UnlockedDesktopActivityCard",
  );
  const { showCredits } = useMembershipRequirement();
  const { ageRangeString, classSizeString } = useActivityString();
  const {
    classDurationShortString,
    classFormatString,
    weeklyMeetingsSearchString,
    videoMeetingsString,
  } = usePriceDetailsString();
  const { tutoringShowAvailableTimesInline } = useTutoringFlow();

  const { selfElectedStateFundingProgram } =
    useSelfElectedStateFundingProgram();

  const shouldShowFundingBanner = (() => {
    if (!selfElectedStateFundingProgram?.stateAbbreviation) {
      return false;
    }
    const stateAbbreviation =
      selfElectedStateFundingProgram.stateAbbreviation.toLowerCase();
    return activity.attributes?.some(
      attr =>
        attr.key === `funding_program_approved_${stateAbbreviation}` &&
        attr.value === "true",
    );
  })();

  const seeAvailableTimesButtonRef = React.useRef<HTMLButtonElement>(null);
  const { headerHeight: mainHeaderHeight } = useStickyHeader();
  const scrollToElement = useScrollToElement({
    desktopOffset: mainHeaderHeight + 80,
  });
  const onClickViewGroupClassAvailability = useCallback(() => {
    if (sectionsRef?.current) {
      scrollToElement(sectionsRef.current);
      seeAvailableTimesButtonRef.current?.blur();
    } else if (setIsAvailabilityModalOpen) {
      setIsAvailabilityModalOpen(true);
    }
  }, [sectionsRef, scrollToElement, setIsAvailabilityModalOpen]);
  const onClickViewTutoringAvailability = useCallback(() => {
    if (viewAvailabilityRef?.current) {
      scrollToElement(viewAvailabilityRef.current);
    } else if (setIsAvailabilityModalOpen) {
      setIsAvailabilityModalOpen(true);
    }
  }, [viewAvailabilityRef, scrollToElement, setIsAvailabilityModalOpen]);

  const callToActionMemo = useMemo(() => {
    if (isReadOnly) {
      return null;
    }

    const size = "large";
    // on-demand flex
    if (activity.isSelfPaced) {
      return (
        <SectionCallToAction
          activity={activity}
          section={{}}
          variant={"contained"}
          sx={{ width: "100%" }}
          trackingSharedProperties={{
            component: "DesktopActivityCard",
          }}
          size={size}
        />
      );
    }

    if (activity.wasPublishedInThePast) {
      return (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is no longer offered`}
          </Typography>
          <TrackedButton
            variant="contained"
            onClick={onClickMoreLikeThis}
            sx={{ width: "100%" }}
            trackingName="cdp_see_more_like_this"
            size={size}
          >
            {t`See More Like This`}
          </TrackedButton>
        </>
      );
    }
    // tutoring
    if (activity.hasTeacherSchedule) {
      if (leader.hasBackgroundCheckExpired && !leader.ownerOfSellerOrg) {
        return (
          <>
            <Typography
              variant="subtitle1"
              sx={{ color: "error.600", marginBottom: "1em" }}
            >
              {t`This class is temporarily unavailable`}
            </Typography>
            {onClickMoreLikeThis && (
              <TrackedButton
                variant="contained"
                onClick={onClickMoreLikeThis}
                sx={{ width: "100%" }}
                trackingName="cdp_see_more_like_this"
                size={size}
              >
                {t`See More Like This`}
              </TrackedButton>
            )}
          </>
        );
      }
      // enrollable times are available or loading
      if (
        hasOneOnOneAvailabilities(activityWithEnrollableTimes) ||
        !activityWithEnrollableTimes
      ) {
        if (!tutoringShowAvailableTimesInline) {
          return (
            <TutorEnrollButtonAndModal
              activity={activity}
              buttonText={t("See All Available Times")}
              activityWithEnrollableTimes={activityWithEnrollableTimes}
            />
          );
        }
        return (
          <TrackedButton
            variant={"contained"}
            onClick={onClickViewTutoringAvailability}
            sx={{ width: "100%" }}
            trackingName="cdp_see_available_times_tutoring"
            trackingUniqueId={activity.uid}
            size={size}
          >
            {t`See All Available Times`}
          </TrackedButton>
        );
      }
      return (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is sold out`}
          </Typography>
          <ChooseATimeOrClassScheduleRequestButton
            activity={activity}
            leader={leader}
            variant="contained"
            sx={{ width: "100%" }}
            /* eslint-disable-next-line i18next/no-literal-string */
            locationRenderedOn="DesktopHeaderCallout"
          />
        </>
      );
    }

    // chat group
    if (activity.isClub) {
      return (
        <SectionCallToAction
          activity={activity}
          section={activity.clubSection}
          trackingSharedProperties={{
            component: "DesktopActivityCard",
          }}
          sx={{ width: "100%" }}
          size={size}
        />
      );
    }

    // group class
    if (
      activity.canEnroll ||
      activity.allows_late_enrollments_for_fixed_length_classes
    ) {
      return (
        <TrackedButton
          ref={seeAvailableTimesButtonRef}
          variant={"contained"}
          onClick={onClickViewGroupClassAvailability}
          sx={{ width: "100%" }}
          trackingName="cdp_see_available_times_group"
          trackingUniqueId={activity.uid}
          size={size}
        >
          {t`See All Available Times`}
        </TrackedButton>
      );
    }

    return (
      <>
        {activity.isSoldOut && !activity.allowWaitlist && (
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is sold out`}
          </Typography>
        )}
        <ChooseATimeOrClassScheduleRequestButton
          activity={activity}
          leader={leader}
          variant="contained"
          sx={{ width: "100%" }}
          /* eslint-disable-next-line i18next/no-literal-string */
          locationRenderedOn="DesktopHeaderCallout"
        />
      </>
    );
  }, [
    activity,
    leader,
    isReadOnly,
    onClickViewTutoringAvailability,
    onClickViewGroupClassAvailability,
    onClickMoreLikeThis,
    t,
    tutoringShowAvailableTimesInline,
    activityWithEnrollableTimes,
  ]);

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          flexShrink: 0,
          width: "320px",
          borderRadius: "1em",
          boxShadow: "0px 4px 8px 0px #0000000F, 0px 0px 4px 0px #0000000A",
          [theme.breakpoints.down("lg")]: {
            width: "290px",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.50",
            padding: "32px 20px 20px 20px",
            borderRadius: "1em 1em 0 0",
          }}
        >
          <Typography variant="h6" emphasized>
            {classFormatString(activity)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.25em",
            margin: "1em",
          }}
        >
          {showCredits ? (
            <PriceCreditsLineItem activity={activity} />
          ) : (
            <PriceLineItem activity={activity} />
          )}
        </Box>
        {shouldShowFundingBanner && (
          <Box sx={{ margin: "1em" }}>
            <Box
              sx={{
                backgroundColor: "primary.50",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "primary.200",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "primary.700",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {t(
                  "We accept State funding. Add your learner to begin enrollment",
                )}
              </Typography>
            </Box>
          </Box>
        )}
        <Divider sx={{ borderColor: "neutral.200" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.25em",
            margin: "1em",
          }}
        >
          {activity.isSelfPaced ? (
            <SelfPacedIconBullets activity={activity} />
          ) : (
            <>
              <IconBullet
                icon={faCalendarAlt}
                iconSx={{ color: "grey.600" }}
                text={weeklyMeetingsSearchString(activity)}
              />
              {!activity.isClub && (
                <IconBullet
                  icon={faClock}
                  iconSx={{ color: "grey.600" }}
                  text={classDurationShortString(activity)}
                />
              )}
            </>
          )}
          {activity.isClub && (
            <IconBullet
              icon={faCheck}
              iconSx={{ color: "grey.600" }}
              text={t("{{count}} active learner", {
                count: activity.clubSection?.validEnrollmentCount ?? 0,
                defaultValue_plural: "{{count}} active learners",
              })}
            />
          )}
          {!activity.isSelfPaced && (
            <IconBullet
              icon={
                !activity.isFlexSchedule && !activity.isClub
                  ? faSignalStream
                  : faVideoSlash
              }
              iconSx={{ color: "grey.600" }}
              text={videoMeetingsString(activity)}
            />
          )}
          <IconBullet
            icon={faBirthdayCake}
            iconSx={{ color: "grey.600" }}
            text={ageRangeString(activity)}
          />
          {!activity.isClub &&
            !activity.isFlexSchedule &&
            activity.size_max != 1 && (
              <IconBullet
                icon={faUsers}
                iconSx={{ color: "grey.600" }}
                text={t("{{learnerCount}} learners per class", {
                  learnerCount: classSizeString(activity),
                })}
              />
            )}
        </Box>
        {!isReadOnly && (
          <>
            <Divider sx={{ borderColor: "neutral.200" }} />
            <Box
              flex
              sx={{ padding: "1em", flexDirection: "column", gap: "0.5em" }}
            >
              {callToActionMemo && (
                <Box
                  sx={{
                    textAlign: "center",
                  }}
                  data-test-id="card-cta"
                >
                  {callToActionMemo}
                </Box>
              )}
              {isLoggedIn && !isReadOnly && (
                <AskAQuestionButton activity={activity} leader={leader} />
              )}
            </Box>
          </>
        )}
      </Box>
      {showCredits && !hideCreditDescription && (
        <Box
          sx={{
            padding: "16px",
            paddingTop: "32px",
            color: "grey.600",
            fontSize: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Trans t={t}>
            <Typography emphasized variant="body2">
              <Icon icon={faLightbulb} /> Understanding Outschool Credits
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 280 }}>
              Each credit is worth 50¢ towards classes. Higher-tier plans unlock
              bigger discounts, so your family gets more for less.
            </Typography>
          </Trans>
        </Box>
      )}
    </>
  );
}
